import { LockSimpleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { LeanTag } from '~/table/classic/LeanTag.js';
import { ButtonLink } from '~/ui/Button.js';
import { FieldLabel } from '~/ui/FieldLabel.js';
import { offerFrom } from './subscriptionPages.js';

/**
 * The members-only marks in Settings, graduated from the `game-paywall`
 * variant of the settings sketch: the tag pinned to a style's preview, and the
 * line under a picker that is showing a paid one.
 *
 * Browsing is never blocked. A free player sees every style, and a paid one is
 * a preview only: nothing is saved while it shows (`PreferenceRows.tsx`).
 */

/** Which locked door opened the offer. */
export type OfferDoor = 'front' | 'back' | 'board' | 'avatar';

/** The focus key a door's See the offer chip wears, so a way back lands on it. */
export function offerFocusKey(door: OfferDoor): string {
  return `offer-${door}`;
}

/**
 * Where See the offer comes back to: the page it was pressed on, and the
 * `returnTo` that page arrived with this time.
 */
export interface OfferReturn {
  /** The path Back on the offer returns to. */
  readonly backTo: string;
  /** The `returnTo` this page arrived with, which says it is back from the offer. */
  readonly returnTo?: string | undefined;
}

/*
 * The preview each door's See the offer chip left with. Leaving the page drops
 * a preview, as the sketch says, but a trip to the offer and back is not
 * leaving: the player comes back to the style they asked about, and the chip
 * they pressed is there again for focus to land on.
 */
const parked = new Map<OfferDoor, string>();

/** The preview this door left for the offer, when the page is arriving back from it. */
export function parkedPreview(door: OfferDoor, offer: OfferReturn | undefined): string | null {
  if (offer?.returnTo !== offerFocusKey(door)) return null;
  return parked.get(door) ?? null;
}

/**
 * In use on a free style, Members on a style a free player can only look at.
 * The leaning tag the Classic hint and review hang on a choice, pinned to the
 * preview's corner, so a paid style is marked the way the table marks a best
 * move. Members is lemon, as the sketch draws it.
 */
export function PaywallTag({ locked }: { readonly locked: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <LeanTag
      label={locked ? t('settings.paywall.members') : t('settings.paywall.inUse')}
      lemon={locked}
      placement="pinned"
    />
  );
}

/**
 * What a free player sees while the preview is a style they cannot use. It
 * names what they keep, which is what the table goes on drawing, and offers
 * the offer.
 *
 * A well of `ground` inside the white Section card, the recipe review-play's
 * ledger uses, at the card's own corner. See the offer is a default chip, not
 * the lemon, because up to three can show at once. Without `offer` the line
 * stands alone: the table's drawer draws it that way, because a trip to the
 * offer from there would leave the game.
 */
export function MemberGate({
  door,
  keeping,
  preview,
  offer,
}: {
  readonly door: OfferDoor;
  /** The hint under Members only, naming what the player keeps. Already translated. */
  readonly keeping: string;
  /** The style on show, kept for the page while the player reads the offer. */
  readonly preview: string;
  readonly offer?: OfferReturn | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const link = offer === undefined ? undefined : offerFrom(offer.backTo);
  return (
    <div className="mb-2 flex min-h-12 items-center gap-3 rounded-3xl bg-ground p-4">
      <LockSimpleIcon size={20} className="shrink-0 text-muted" aria-hidden="true" />
      <FieldLabel hint={keeping} className="flex-1">
        {t('settings.paywall.membersOnly')}
      </FieldLabel>
      {link !== undefined && (
        <ButtonLink
          size="sm"
          className="flex-none"
          to={link.to}
          state={{ ...link.state, returnTo: offerFocusKey(door) }}
          data-focus-key={offerFocusKey(door)}
          onClick={() => {
            parked.set(door, preview);
          }}
        >
          {t('settings.paywall.seeOffer')}
        </ButtonLink>
      )}
    </div>
  );
}
